import { clone, get, setWith } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

export const useStateWithUpdates = <T extends Record<string, unknown>>(
	externalState: T
) => {
	const [state, setState] = useState<T>(externalState);
	const [isDirty, setIsDirty] = useState(false);

	useEffect(() => {
		setState(externalState);
		setIsDirty(false);
	}, [externalState]);

	const setValue = useCallback(
		(value: unknown, path: string) => {
			setState((s) => {
				if (get(s, path) !== value) setIsDirty(true);

				// we need to clone the path so we are using "cloneWith", with "clone" as the customizer
				return setWith({ ...s }, path, value, clone);
			});
		},
		[setState]
	);

	const resetState = useCallback(() => {
		setState(externalState);
		setIsDirty(false);
	}, [externalState]);

	return [state, setValue, resetState, isDirty] as [
		typeof state,
		typeof setValue,
		typeof resetState,
		typeof isDirty
	];
};
